import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trophy, Target as TargetIcon, TrendingUp, TrendingDown } from "lucide-react";

const grades = {
  excellent: { color: "#059669", bg: "rgba(5,150,105,0.15)" },
  good: { color: "#22c55e", bg: "rgba(34,197,94,0.15)" },
  average: { color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  attention: { color: "#DC2626", bg: "rgba(220,38,38,0.15)" },
};

const Scorecard = () => {
  const { receptionists, user } = useOutletContext();
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const [empId, setEmpId] = useState("");
  const [dateFrom, setDateFrom] = useState(first.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user.role === "receptionist" && user.employee_id) {
      setEmpId(user.employee_id);
    } else if (receptionists?.length > 0 && !empId) {
      setEmpId(receptionists[0].employee_id);
    }
  }, [receptionists, user]);

  useEffect(() => {
    if (!empId) return;
    api
      .get(`/scorecard/${empId}?date_from=${dateFrom}&date_to=${dateTo}`)
      .then((r) => setData(r.data));
  }, [empId, dateFrom, dateTo]);

  const g = data ? grades[data.grade?.code] || grades.average : grades.average;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Individual Scoring</div>
        <h1 className="font-heading text-4xl text-white tracking-tight mt-1">Weighted Performance Scorecard</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Every tracked metric compared individually against its monthly target, weighted per the receptionist's profile.
        </p>
      </div>

      <Card className="bg-[#1E293B] border-[#334155] p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">
              Receptionist{" "}
              <span className="text-[#C9A24B] normal-case tracking-normal">({receptionists.length} loaded)</span>
            </Label>
            <Select value={empId || undefined} onValueChange={setEmpId} disabled={user.role === "receptionist"}>
              <SelectTrigger data-testid="score-emp-select" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue placeholder="Select receptionist…" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                {receptionists.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500 italic">No active receptionists — add via Master List</div>
                )}
                {receptionists.map((r) => (
                  <SelectItem key={r.employee_id} value={r.employee_id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">From</Label>
            <Input
              type="date"
              data-testid="score-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </div>
          <div>
            <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">To</Label>
            <Input
              type="date"
              data-testid="score-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </div>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#1E293B] border-[#334155] p-6">
              <div className="overline">Overall Weighted</div>
              <div className="flex items-baseline gap-3 mt-2">
                <div className="font-heading text-5xl tracking-tight" style={{ color: g.color }}>
                  {data.overall_performance}%
                </div>
              </div>
              <Badge
                className="border font-mono mt-3"
                style={{ backgroundColor: g.bg, color: g.color, borderColor: g.color }}
              >
                {data.grade.label}
              </Badge>
              <div className="text-xs text-slate-500 mt-3">
                {data.receptionist.name} · {data.receptionist.employee_id}
              </div>
            </Card>
            <Card className="bg-[#1E293B] border-[#334155] p-6">
              <div className="flex items-center gap-2 overline">
                <TrendingUp size={12} /> Best Column
              </div>
              {data.best_column ? (
                <>
                  <div className="font-heading text-xl text-white mt-2">{data.best_column.label}</div>
                  <div className="text-emerald-400 font-heading text-2xl mt-1">
                    {data.best_column.performance}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Actual {data.best_column.actual} / Target {data.best_column.target}
                  </div>
                </>
              ) : (
                <div className="text-slate-500 text-sm mt-2">Set targets to compute best column.</div>
              )}
            </Card>
            <Card className="bg-[#1E293B] border-[#334155] p-6">
              <div className="flex items-center gap-2 overline">
                <TrendingDown size={12} /> Weakest Column
              </div>
              {data.weakest_column ? (
                <>
                  <div className="font-heading text-xl text-white mt-2">{data.weakest_column.label}</div>
                  <div className="text-rose-400 font-heading text-2xl mt-1">
                    {data.weakest_column.performance}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Actual {data.weakest_column.actual} / Target {data.weakest_column.target}
                  </div>
                </>
              ) : (
                <div className="text-slate-500 text-sm mt-2">Set targets to compute weakest column.</div>
              )}
            </Card>
          </div>

          <Card className="bg-[#1E293B] border-[#334155] p-6" data-testid="scorecard-table">
            <div className="flex items-center gap-2 mb-4">
              <Trophy size={18} className="text-[#C9A24B]" />
              <h3 className="font-heading text-xl text-white">Column-wise Performance</h3>
            </div>
            <div className="rounded-lg border border-[#334155] overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#334155] hover:bg-transparent">
                    <TableHead className="text-slate-400 font-mono text-xs uppercase">Metric</TableHead>
                    <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Target</TableHead>
                    <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Actual</TableHead>
                    <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Weight</TableHead>
                    <TableHead className="text-slate-400 font-mono text-xs uppercase text-right">Performance</TableHead>
                    <TableHead className="text-slate-400 font-mono text-xs uppercase">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r) => {
                    const rowGrade =
                      r.performance >= 100 ? "#059669" : r.performance >= 60 ? "#F59E0B" : "#DC2626";
                    return (
                      <TableRow key={r.metric_key} className="border-[#334155] hover:bg-slate-800/40">
                        <TableCell className="text-white">{r.label}</TableCell>
                        <TableCell className="text-slate-300 text-right font-mono">{r.target}</TableCell>
                        <TableCell className="text-slate-300 text-right font-mono">{r.actual}</TableCell>
                        <TableCell className="text-slate-500 text-right font-mono">{r.weight}</TableCell>
                        <TableCell className="text-right font-mono" style={{ color: rowGrade }}>
                          {r.performance}%
                        </TableCell>
                        <TableCell>
                          <Badge
                            className="border font-mono text-[10px]"
                            style={{
                              backgroundColor: `${rowGrade}22`,
                              color: rowGrade,
                              borderColor: rowGrade,
                            }}
                          >
                            {r.status.replace("_", " ")}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default Scorecard;
